/**
 * Presents one Vault item in the shared table/list contract.
 *
 * The row exposes provenance, version, access, selection, and available actions
 * without owning mutations. This keeps list layout behavior parallel with grid
 * cards and preserves one backend-enforced permission model.
 */
import { useState } from 'react'
import { Download, Info, LockKeyhole, UserPlus } from 'lucide-react'
import {
  Button,
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@/components/ui'
import { VaultItemIcon } from './VaultItemIcon'
import type { ItemResponse } from '../types'
import {
  canEdit,
  formatDate,
  formatSize,
  getDragPayload,
  hasDragPayload,
  hasFilePayload,
  kindLabel,
  setDragPayload,
  type VaultDragPayload,
} from '../lib/format'
import { VaultItemMenu } from './VaultItemMenu'
import { VaultVersionBadge } from './VaultVersionBadge'
import { SourceProviderBadge } from '@/components/SourceProviderBadge'

interface VaultRowProps {
  entry: ItemResponse
  index: number
  trashView: boolean
  checked: boolean
  showSourceBadge: boolean
  onOpen: () => void
  onDownload: () => void
  onShare: () => void
  onRename: () => void
  onMove: () => void
  onCopy: () => void
  onNewVersion: () => void
  onVersionHistory: () => void
  onDetails: () => void
  onTrash: () => void
  onRestore: () => void
  onCheckedChange: (checked: boolean) => void
  onDropItem: (payload: VaultDragPayload, targetFolderId: string) => void
  onDropFiles: (files: File[], targetFolderId: string) => void
}

export function VaultRow({
  entry,
  index,
  trashView,
  checked,
  showSourceBadge,
  onOpen,
  onDownload,
  onShare,
  onRename,
  onMove,
  onCopy,
  onNewVersion,
  onVersionHistory,
  onDetails,
  onTrash,
  onRestore,
  onCheckedChange,
  onDropItem,
  onDropFiles,
}: VaultRowProps) {
  const [dragging, setDragging] = useState(false)
  const [dropTarget, setDropTarget] = useState(false)
  const { item, effectiveRole } = entry
  const editable = canEdit(entry)
  const owner = effectiveRole === 'owner'
  const isFolder = item.kind === 'folder'
  const droppable = isFolder && !trashView && editable

  const acceptDrop = (event: React.DragEvent) =>
    droppable && (hasFilePayload(event) || hasDragPayload(event))

  return (
    <div
      role="row"
      draggable={!trashView && editable}
      onDragStart={(event) => {
        setDragPayload(event, {
          itemId: item.itemId,
          revision: item.revision,
          name: item.name,
          kind: item.kind,
        })
        setDragging(true)
      }}
      onDragEnd={() => setDragging(false)}
      onDragOver={(event) => {
        if (!acceptDrop(event)) return
        event.preventDefault()
        event.stopPropagation()
        setDropTarget(true)
      }}
      onDragLeave={() => setDropTarget(false)}
      onDrop={(event) => {
        if (!droppable) return
        event.preventDefault()
        event.stopPropagation()
        setDropTarget(false)
        const files = Array.from(event.dataTransfer.files)
        if (files.length) return onDropFiles(files, item.itemId)
        const payload = getDragPayload(event)
        if (payload && payload.itemId !== item.itemId) onDropItem(payload, item.itemId)
      }}
      onClick={onOpen}
      className={cn(
        'group flex cursor-pointer items-center gap-3 border-b px-4 py-2.5 transition-all duration-150',
        'animate-in fade-in slide-in-from-bottom-1 fill-mode-both hover:bg-muted/60',
        dragging && 'opacity-40',
        dropTarget && 'bg-primary/5 ring-2 ring-inset ring-primary/60',
      )}
      style={{ animationDelay: `${Math.min(index, 15) * 18}ms` }}
    >
      <span className="flex w-8 shrink-0 items-center justify-center" onClick={(event) => event.stopPropagation()}>
        {!trashView && (
          <Checkbox
            checked={checked}
            onCheckedChange={(value) => onCheckedChange(value === true)}
            aria-label={`Select ${item.name}`}
          />
        )}
      </span>
      <VaultItemIcon
        kind={item.kind}
        name={item.name}
        linked={item.linked || item.readOnly}
        className={cn(
          'transition-transform duration-150 ease-out group-hover:scale-105',
          dropTarget && 'scale-110',
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-sm font-medium">{item.name}</p>
          {showSourceBadge && <SourceProviderBadge provider={item.sourceConnector} />}
          {item.metadata.read_only_badge === 'true' && (
            <span className="inline-flex h-5 shrink-0 items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2 text-[10px] font-semibold text-slate-500">
              <LockKeyhole className="h-2.5 w-2.5" /> Read-only
            </span>
          )}
          {!trashView && item.kind === 'file'
            && item.currentVersionNumber !== undefined && (
            <VaultVersionBadge
              name={item.name}
              versionNumber={item.currentVersionNumber}
              onOpen={onVersionHistory}
            />
          )}
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {kindLabel(entry)}
          {effectiveRole !== 'owner' && ` · Shared by ${item.ownerUserId}`}
        </p>
      </div>
      <span className="hidden w-24 shrink-0 text-right text-xs text-muted-foreground sm:block">
        {formatDate(item.updatedAt)}
      </span>
      <span className="hidden w-20 shrink-0 text-right text-xs text-muted-foreground md:block">
        {item.kind === 'file' ? formatSize(item.sizeBytes) : '—'}
      </span>
      <div
        className="flex w-36 shrink-0 items-center justify-end gap-0.5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-0.5 transition-all duration-150 ease-out sm:translate-x-1 sm:opacity-0 sm:group-focus-within:translate-x-0 sm:group-focus-within:opacity-100 sm:group-hover:translate-x-0 sm:group-hover:opacity-100">
          {!trashView && item.kind === 'file' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 transition-all active:scale-90" aria-label={`Download ${item.name}`} onClick={onDownload}>
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download</TooltipContent>
            </Tooltip>
          )}
          {!trashView && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 transition-all active:scale-90" aria-label={`Details for ${item.name}`} onClick={onDetails}>
                  <Info className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Details</TooltipContent>
            </Tooltip>
          )}
          {!trashView && owner && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 transition-all active:scale-90" aria-label={`Share ${item.name}`} onClick={onShare}>
                  <UserPlus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Share</TooltipContent>
            </Tooltip>
          )}
        </div>
        <VaultItemMenu
          entry={entry}
          trashView={trashView}
          onDownload={onDownload}
          onShare={onShare}
          onRename={onRename}
          onMove={onMove}
          onCopy={onCopy}
          onNewVersion={onNewVersion}
          onVersionHistory={onVersionHistory}
          onTrash={onTrash}
          onRestore={onRestore}
        />
      </div>
    </div>
  )
}
